import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Typography from '@material-ui/core/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import GitHubIcon from '@mui/icons-material/GitHub';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolIcon from '@mui/icons-material/School';
import './styles/projects.css';

export const Projects = ({ darkMode }) => {

  const projects = [
    {
      title: "Entreno",
      category: "Full-Stack / AI",
      description: "AI-powered fitness platform where an LLM agent dynamically generates workout programs, provides coaching, and adapts to user performance. Features encrypted per-user memory (AES-256-GCM), autoregulation engine, conversational coach with approval workflows, and a complete routine/progress tracking system.",
      tech: ["Python", "FastAPI", "PydanticAI", "PostgreSQL", "Docker", "Terraform", "Azure", "Stripe"],
      liveUrl: "https://dev.entreno.io/",
      githubUrl: "https://github.com/hernanpesantez/",
    },
    {
      title: "ML Trading Strategies",
      category: "Machine Learning",
      description: "Evaluated algorithmic trading strategies for JPM stock. Built a Manual Strategy with five technical indicators (BBP, RSI, Momentum, MACD, CCI) and a Strategy Learner using Bagged Random Trees. Conducted controlled experiments comparing in-sample vs. out-of-sample performance and market impact analysis.",
      tech: ["Python", "NumPy", "pandas", "scikit-learn", "Matplotlib"],
      isAcademic: true,
      course: "Georgia Tech — CS 7646 ML for Trading (Fall 2025)",
    },
    {
      title: "Q-Learning Trading Robot",
      category: "Reinforcement Learning",
      description: "Trained a Q-learning agent to navigate grid-world environments and learn optimal trading policies. Implemented the full RL pipeline including state-action value learning, exploration-exploitation tradeoff via epsilon-greedy, and convergence analysis.",
      tech: ["Python", "NumPy", "Q-Learning", "Reinforcement Learning"],
      isAcademic: true,
      course: "Georgia Tech — CS 7646 ML for Trading (Fall 2025)",
    },
    {
      title: "Technical Indicator Evaluation",
      category: "Quantitative Finance",
      description: "Comprehensive analysis of five technical trading indicators: Bollinger Band Percentage, CCI, MACD, RSI, and Momentum. Implemented a Theoretically Optimal Strategy with perfect foresight as a benchmark, and evaluated each indicator's signals, strengths, and suitability for different market conditions.",
      tech: ["Python", "pandas", "NumPy", "Matplotlib"],
      isAcademic: true,
      course: "Georgia Tech — CS 7646 ML for Trading (Fall 2025)",
    },
    {
      title: "CART Regression & Ensemble Learning",
      category: "Machine Learning",
      description: "Evaluated CART-based regression algorithms on the Istanbul Stock Exchange dataset. Compared Decision Trees, Random Trees, and Bootstrap Aggregation (Bagging) to analyze the bias-variance tradeoff, overfitting behavior, and how randomization impacts accuracy and training efficiency.",
      tech: ["Python", "NumPy", "Matplotlib", "LaTeX"],
      isAcademic: true,
      course: "Georgia Tech — CS 7646 ML for Trading (Fall 2025)",
    },
    {
      title: "Portfolio Optimization",
      category: "Quantitative Finance",
      description: "Built a portfolio optimizer from scratch that finds optimal asset allocations to maximize Sharpe Ratio. Analyzed portfolio performance metrics including daily returns, volatility, and risk-adjusted returns.",
      tech: ["Python", "NumPy", "pandas", "Matplotlib"],
      isAcademic: true,
      course: "Georgia Tech — CS 7646 ML for Trading (Fall 2025)",
    },
    {
      title: "Graph Search & Pathfinding",
      category: "Artificial Intelligence",
      description: "Implemented BFS, DFS, uniform-cost search, and A* with custom admissible heuristics for optimal route planning on the Romania road network. Focused on search efficiency, optimality guarantees, and heuristic design.",
      tech: ["Python", "NetworkX", "A*", "BFS", "DFS"],
      isAcademic: true,
      course: "Georgia Tech — CS 6601 Artificial Intelligence (Spring 2026)",
    },
    {
      title: "Adversarial Game-Playing Agents",
      category: "Artificial Intelligence",
      description: "Built AI agents for Two-Rook Isolation using adversarial search. Implemented minimax with alpha-beta pruning, iterative deepening, and custom evaluation heuristics to play competitively against opposing agents.",
      tech: ["Python", "Minimax", "Alpha-Beta Pruning", "Game Trees"],
      isAcademic: true,
      course: "Georgia Tech — CS 6601 Artificial Intelligence (Spring 2026)",
    },
    {
      title: "Bayesian Networks & MCMC Sampling",
      category: "Artificial Intelligence",
      description: "Built Bayesian networks for probabilistic reasoning and implemented Gibbs sampling and Metropolis-Hastings (MCMC) from scratch. Compared convergence rates of sampling methods against exact inference on a security threat model.",
      tech: ["Python", "pgmpy", "Gibbs Sampling", "Metropolis-Hastings", "MCMC"],
      isAcademic: true,
      course: "Georgia Tech — CS 6601 Artificial Intelligence (Spring 2026)",
    },
    {
      title: "Decision Trees & Multiclass Classification",
      category: "Artificial Intelligence",
      description: "Implemented decision trees from scratch for multiclass classification including Gini impurity splitting, pruning, random forests, and k-fold cross-validation. Built confusion matrices, precision/recall metrics, and vectorized operations for large datasets.",
      tech: ["Python", "NumPy", "Decision Trees", "Random Forests", "Vectorization"],
      isAcademic: true,
      course: "Georgia Tech — CS 6601 Artificial Intelligence (Spring 2026)",
    },
    {
      title: "Expectation Maximization & Image Segmentation",
      category: "Artificial Intelligence",
      description: "Implemented k-means clustering and Gaussian Mixture Models trained with EM from scratch for image segmentation and 3D point cloud clustering. Built the full pipeline including log-likelihood scoring, model selection via Bayesian Information Criterion (BIC), and improved initialization strategies.",
      tech: ["Python", "NumPy", "K-Means", "GMM", "EM Algorithm", "BIC"],
      isAcademic: true,
      course: "Georgia Tech — CS 6601 Artificial Intelligence (Spring 2026)",
    },
    {
      title: "Hidden Markov Models & Sign Language Recognition",
      category: "Artificial Intelligence",
      description: "Built a word recognizer for American Sign Language video sequences using Hidden Markov Models. Implemented the Viterbi algorithm from scratch to find the most likely state sequences, with both 1D and multidimensional observation models using hand and thumb tracking coordinates.",
      tech: ["Python", "NumPy", "HMM", "Viterbi Algorithm", "Pattern Recognition"],
      isAcademic: true,
      course: "Georgia Tech — CS 6601 Artificial Intelligence (Spring 2026)",
    },
    {
      title: "Browser Automation with LLMs",
      category: "AI / Automation",
      description: "Testing framework for LLM-powered browser automation. Features an interactive menu-driven test suite with pre-built scenarios (web search, Reddit interaction, news scraping, Wikipedia research) and custom task execution through natural language prompts.",
      tech: ["Python", "Playwright", "OpenAI", "LangChain", "FastAPI"],
      githubUrl: "https://github.com/hernanpesantez/",
    },
  ];

  return (
    <Container className={darkMode ? 'container-dark' : 'container-light'}>

      <Box className={darkMode ? 'box-dark' : 'box-light'} sx={{ pt: 4, pb: 2 }}>
        <Typography variant="h5" className="box-about-text" style={{ textAlign: 'center' }}>
          A selection of personal and academic projects spanning full-stack development, cloud infrastructure, and machine learning.
        </Typography>
      </Box>

      <Divider className={darkMode ? 'divider-dark' : 'divider-light'} />

      <Box className="projects-grid">
        {projects.map((project, index) => (
          <Card key={index} className={darkMode ? 'card-project-dark' : 'card-project-light'}>
            <CardActionArea
              href={project.liveUrl || project.githubUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="card-action-area"
              disabled={!project.liveUrl && !project.githubUrl}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography gutterBottom variant="h5" component="div" className="project-card-title" style={{ marginBottom: 0 }}>
                    {project.title}
                  </Typography>
                  {project.isAcademic && (
                    <SchoolIcon fontSize="small" className={darkMode ? 'icon-academic-dark' : 'icon-academic-light'} />
                  )}
                </Box>
                <Typography variant="caption" className={darkMode ? 'category-dark' : 'category-light'}>
                  {project.category}
                </Typography>
                <Typography variant="body2" color="text.secondary" className={darkMode ? 'card-text-dark' : ''} sx={{ mt: 1 }}>
                  {project.description}
                </Typography>
                <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {project.tech.map((t, i) => (
                    <Chip
                      key={i}
                      label={t}
                      size="small"
                      className={darkMode ? 'chip-dark' : 'chip-light'}
                    />
                  ))}
                </Box>
              </CardContent>
            </CardActionArea>
            <CardActions>
              {project.liveUrl && (
                <Link href={project.liveUrl} target="_blank" rel="noopener noreferrer">
                  <OpenInNewIcon className={darkMode ? "icon-dark" : "icon-light"} />
                </Link>
              )}
              {project.githubUrl && (
                <Link href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                  <GitHubIcon className={darkMode ? "icon-dark" : "icon-light"} />
                </Link>
              )}
              {project.isAcademic && (
                <Typography variant="caption" className={darkMode ? 'academic-note-dark' : 'academic-note-light'}>
                  {project.course || "Georgia Tech coursework"}
                </Typography>
              )}
            </CardActions>
          </Card>
        ))}
      </Box>

    </Container>
  );
};
